import { Controller } from '@hotwired/stimulus';
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';

export default class extends Controller {
  static targets = ['modal', 'container', 'content', 'swiperModalContent', 'prevButton', 'nextButton'];
  static values = {
    index: Number,
  };

  connect() {
    // Don't initialize Swiper immediately - wait for modal to open
    this.modalInitialized = false;

    // Observe for new content targets to handle pagination
    this.recalculateIndexes();
  }

  initSwiper() {
    if (this.modalInitialized) return;

    this.swiper = new Swiper(this.containerTarget, {
      modules: [Navigation, Pagination],
      slidesPerView: 1,
      spaceBetween: 0,
      centeredSlides: true,
      loop: false,
      keyboard: {
        enabled: true,
      },
      navigation: {
        nextEl: this.hasNextButtonTarget
          ? this.nextButtonTarget
          : '.swiper-button-next',
        prevEl: this.hasPrevButtonTarget
          ? this.prevButtonTarget
          : '.swiper-button-prev',
      },
    });

    if (this.hasIndexValue) {
      this.swiper.update();
      this.swiper.slideTo(this.indexValue, 0);
    }

    // Add event listeners for slide changes to handle video loading
    this.swiper.on('slideChange', () => {
      this.handleSlideChange();
    });

    // Also handle the initial slide
    this.handleSlideChange();

    this.modalInitialized = true;
  }

  handleSlideChange() {
    if (!this.swiper) return;

    const activeIndex = this.swiper.activeIndex;
    const activeSlide = this.swiperModalContentTargets[activeIndex];

    if (!activeSlide) return;

    // If this is a video slide, load and play the video
    const videoContainer = activeSlide.querySelector('[data-video-url]');
    if (videoContainer && !videoContainer.querySelector('video')) {
      this.loadVideo(null, videoContainer);
    }

    // Pause videos in other slides
    this.swiperModalContentTargets.forEach((slide, index) => {
      if (index !== activeIndex) {
        const video = slide.querySelector('video');
        if (video && !video.paused) {
          video.pause();
        }
      }
    });
  }

  contentTargetConnected(element) {
    this.recalculateIndexes();
    // If swiper is already initialized, update it to include new slides
    if (this.swiper) {
      this.swiper.update();
    }
  }

  disconnect() {
    if (this.swiper) {
      this.swiper.destroy();
      this.modalInitialized = false;
    }
  }

  recalculateIndexes() {
    if (this.hasContentTarget) {
      this.contentTargets.forEach((content, index) => {
        content.dataset.index = index;
      });
    }
  }

  loadVideo(event, containerElement = null) {
    // If called from an event, prevent default and get container from event
    // Otherwise use the provided container element (from handleSlideChange)
    let container;

    if (event) {
      event.preventDefault();
      event.stopPropagation();
      container = event.currentTarget;
    } else if (containerElement) {
      container = containerElement;
    } else {
      return; // No container provided
    }

    const videoUrl = container.dataset.videoUrl;

    if (!videoUrl || container.querySelector('video')) {
      return; // Already loaded or no video URL
    }

    // Get the thumbnail image if it exists
    const thumbnailImg = container.querySelector('img');
    let posterUrl = '';
    if (thumbnailImg) {
      posterUrl = thumbnailImg.src;
    }

    // Remove the play icon
    const playIcon = container.querySelector('div');
    if (playIcon) {
      playIcon.remove();
    }

    // Create video element
    const video = document.createElement('video');
    video.controls = true;
    video.className = 'max-w-full max-h-[100dvh]';
    video.autoplay = true;

    // Set the poster if we have a thumbnail
    if (posterUrl) {
      video.poster = posterUrl;
    }

    // Create source element with video URL
    const source = document.createElement('source');
    source.src = videoUrl;
    // source.type = 'video/mp4';

    // Add unsupported message
    const unsupportedText = document.createTextNode(
      'Your browser does not support the video tag.'
    );

    // Assemble and add to DOM
    video.appendChild(source);
    video.appendChild(unsupportedText);
    container.innerHTML = '';
    container.appendChild(video);
  }

  openModal(event) {
    event.preventDefault();

    const clickedIndex = parseInt(event.currentTarget.dataset.index || 0);
    this.indexValue = clickedIndex;

    // Show the modal
    this.modalTarget.classList.remove('hidden');
    document.body.classList.add('overflow-hidden');

    this.initSwiper();

    if (this.swiper) {
      this.swiper.update();
      this.swiper.slideTo(clickedIndex, 0);
    }
  }

  // Prevent modal from opening when clicking inside the video generation modal
  preventOpenModal(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  close(event) {
    if (event) event.preventDefault();

    this.modalTarget.classList.add('hidden');

    // Stop all videos when closing modal
    const videos = this.element.querySelectorAll('video');
    videos.forEach((video) => {
      if (!video.paused) {
        video.pause();
      }
    });

    document.body.classList.remove('overflow-hidden');
  }

  // Close modal when clicking outside the content
  closeBackground(event) {
    if (event.target === this.modalTarget) {
      this.close();
    }
  }

  // Handle keyboard events
  keydown(event) {
    if (event.key === 'Escape') {
      this.close();
    }
  }
}
